'use client';

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "next/navigation";
import { useRouter } from "nextjs-toploader/app";
import Link from "next/link";
import {
    ArrowLeft,
    File as FileIcon,
    FileText,
    History,
    LayoutGrid,
    LoaderCircle,
    Package,
    Search,
    SearchX,
    X,
} from "lucide-react";

import type { SearchTotals } from "@/server/search/search.service";
import { suggestSearch } from "@/server/search/suggest.client";
import {
    clearRecentSearches,
    deleteRecentSearch,
    fetchRecentSearches,
    saveRecentSearch,
} from "@/server/search/recent.client";
import type { Suggestions } from "@/types/search";
import { formatNumber } from "@/lib/utils";

/* ================================
   Types & constants
================================ */

const EMPTY_SUGGESTIONS: Suggestions = {
    posts: [],
    products: [],
    files: [],
};

type Kind = "product" | "post" | "file";
type TabKey = "all" | "products" | "posts" | "files";

interface ResultItem {
    key: string;
    kind: Kind;
    title: string;
    /** متن کوچک زیر عنوان (مثلاً SKU) */
    sub?: string;
    href: string;
    /** آدرس کامل عکس */
    image?: string;
    /** فایل‌ها در تب جدید باز می‌شوند */
    external?: boolean;
}

const KIND_ORDER: Kind[] = ["product", "post", "file"];

const KIND_META: Record<Kind, { label: string; tab: Exclude<TabKey, "all"> }> = {
    product: { label: "محصولات", tab: "products" },
    post: { label: "مقالات", tab: "posts" },
    file: { label: "فایل‌ها", tab: "files" },
};

const TAB_KIND: Record<Exclude<TabKey, "all">, Kind> = {
    products: "product",
    posts: "post",
    files: "file",
};

/** حداکثر تعداد جستجوهای اخیر نمایش‌داده‌شده (ذخیره در دیتابیس) */
const RECENT_MAX = 30;
/** اگر عبارت جدید ظرف این مدت ادامه/کوتاه‌شده‌ی عبارت قبلی باشد، به‌جای آن ثبت می‌شود */
const RECENT_REPLACE_WINDOW_MS = 20000;

/** تعداد آیتم در هر درخواست (صفحه) برای اسکرول بی‌نهایت */
const PAGE_SIZE = 20;

const EMPTY_TOTALS: SearchTotals = { products: 0, posts: 0, files: 0 };
const FIRST_PAGES: Record<Kind, number> = { product: 1, post: 1, file: 1 };

/** حذف موارد تکراری هنگام اضافه کردن صفحه‌ی بعد */
const mergeUnique = (prev: unknown[], next: unknown[]): unknown[] => {
    const keyOf = (x: unknown) => {
        const o = x as { id?: string | number; name?: string };
        return String(o?.id ?? o?.name ?? JSON.stringify(x));
    };
    const seen = new Set(prev.map(keyOf));
    return [...prev, ...next.filter((x) => !seen.has(keyOf(x)))];
};

/** در تب «همه» از هر دسته فقط چند مورد اول نمایش داده می‌شود */
const ALL_TAB_LIMIT: Record<Kind, number> = { product: 8, post: 4, file: 4 };

/** همان آدرسی که برای PDF ها درست کار می‌کند (بدون اسلش انتهایی) */
const STORAGE = (process.env.NEXT_PUBLIC_STORAGE_UPLOADS || "").replace(/\/+$/, "");

/** پوشه‌ی عکس محصولات و مقالات (همان env هایی که ProductGrid / PostGrid استفاده می‌کنند) */
const IMAGE_BASE: Record<"product" | "post", string> = {
    product: (process.env.NEXT_PUBLIC_STORAGE_UPLOADS_PRODUCT || "").replace(/\/+$/, ""),
    post: (process.env.NEXT_PUBLIC_STORAGE_UPLOADS_POST || "").replace(/\/+$/, ""),
};
const IMAGE_EXT = process.env.NEXT_PUBLIC_STORAGE_EXTENSION_PICTURE || "webp";

/** فیلدهای احتمالی عکس در پاسخ API (primary_image اولویت دارد) */
const IMAGE_KEYS = ["primary_image", "image", "thumbnail", "thumb", "cover", "featured_image", "photo", "picture", "img"];

/** از آبجکت پیشنهاد، مقدار عکس را (رشته یا {url}/{path}) بیرون می‌کشد */
const pickImage = (obj: unknown): string | undefined => {
    const o = obj as Record<string, unknown>;
    for (const k of IMAGE_KEYS) {
        const v = o?.[k];
        if (typeof v === "string" && v.trim()) return v;
        if (v && typeof v === "object") {
            const inner = v as Record<string, unknown>;
            const s = inner.url || inner.path || inner.src || inner.name;
            if (typeof s === "string" && s) return s;
        }
    }
    return undefined;
};

/**
 * ساخت آدرس عکس دقیقاً مثل لیست محصولات/مقالات:
 *   {NEXT_PUBLIC_STORAGE_UPLOADS_PRODUCT|POST}/{اولین مقدار primary_image}.{پسوند}?w=300
 * - primary_image می‌تواند چند نام جداشده با «,» باشد؛ اولی استفاده می‌شود.
 * - آدرس کامل (http...) بدون تغییر برگردانده می‌شود.
 */
const imageUrl = (kind: "product" | "post", raw?: string) => {
    if (!raw) return undefined;
    const first = raw.split(",")[0].trim();
    if (!first) return undefined;
    if (/^https?:\/\//i.test(first)) return first;

    const clean = first.replace(/^\/+/, "");
    const hasExt = /\.(avif|webp|png|jpe?g|gif|svg)$/i.test(clean);
    return `${IMAGE_BASE[kind]}/${clean}${hasExt ? "" : `.${IMAGE_EXT}`}?w=300`;
};

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** انتخاب تب پیش‌فرض: اول محصولات، اگر نبود اولین دسته‌ای که نتیجه دارد */
const pickDefaultTab = (d: Suggestions): TabKey => {
    if (d.products.length > 0) return "products";
    if (d.posts.length > 0) return "posts";
    if (d.files.length > 0) return "files";
    return "products";
};

/* ================================
   Small components
================================ */

/** پررنگ کردن عبارت جستجوشده داخل عنوان */
const Highlight = ({ text, query }: { text: string; query: string }) => {
    const q = query.trim();
    if (!q) return <>{text}</>;

    const parts = text.split(new RegExp(`(${escapeRegExp(q)})`, "gi"));
    return (
        <>
            {parts.map((part, i) =>
                i % 2 === 1 ? (
                    <mark
                        key={i}
                        className="rounded bg-amber-200/80 px-0.5 text-inherit dark:bg-amber-400/25 dark:text-amber-200"
                    >
                        {part}
                    </mark>
                ) : (
                    <span key={i}>{part}</span>
                ),
            )}
        </>
    );
};

const KIND_STYLE: Record<Kind, { tile: string; icon: React.ReactNode }> = {
    product: {
        tile: "bg-primary/10 text-primary dark:bg-indigo-400/10 dark:text-indigo-200",
        icon: <Package className="h-7 w-7" aria-hidden="true" />,
    },
    post: {
        tile: "bg-emerald-500/10 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-300",
        icon: <FileText className="h-7 w-7" aria-hidden="true" />,
    },
    file: {
        tile: "bg-rose-500/10 text-rose-600 dark:bg-rose-400/10 dark:text-rose-300",
        icon: <FileIcon className="h-7 w-7" aria-hidden="true" />,
    },
};

const ResultCard = ({
                        item,
                        index,
                        active,
                        query,
                        idPrefix = "",
                        onHover,
                        onOpen,
                    }: {
    item: ResultItem;
    index: number;
    active: boolean;
    query: string;
    idPrefix?: string;
    onHover: (index: number) => void;
    onOpen: () => void;
}) => {
    const style = KIND_STYLE[item.kind];
    const [imgFailed, setImgFailed] = useState(false);
    const showImage = !!item.image && !imgFailed;

    const className =
        "group flex h-full flex-col gap-3 rounded-xl border bg-[#fff] p-3 text-right shadow-sm transition duration-200 " +
        "hover:-translate-y-0.5 hover:shadow-md " +
        "dark:bg-[#151b31] dark:shadow-none dark:hover:bg-[#1a2140] " +
        (active
            ? "border-primary/50 ring-2 ring-primary/20 shadow-md dark:border-indigo-300/50 dark:ring-indigo-300/20"
            : "border-gray-200 dark:border-white/10 dark:hover:border-white/20");

    const body = (
        <>
            {/* عکس */}
            <span
                className={
                    "relative flex w-full items-center justify-center overflow-hidden rounded-lg " +
                    (item.kind === "post" ? "aspect-[16/10] " : "aspect-square ") +
                    (showImage ? "bg-[#f6f7fb]" : "bg-gray-100 dark:bg-white/5")
                }
            >
                {showImage ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                        src={item.image}
                        alt={item.title}
                        loading="lazy"
                        onError={() => setImgFailed(true)}
                        className={
                            "h-full w-full transition duration-300 group-hover:scale-105 " +
                            (item.kind === "post" ? "object-cover" : "object-contain p-1.5")
                        }
                    />
                ) : (
                    <span className={`flex h-16 w-16 items-center justify-center rounded-2xl ${style.tile}`}>
                        {style.icon}
                    </span>
                )}

                {item.kind === "file" && (
                    <span className="absolute left-2 top-2 rounded bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                        PDF
                    </span>
                )}
            </span>

            {/* متن */}
            <span className="min-w-0">
                <span className="block line-clamp-2 min-h-10 text-[13px] font-semibold leading-5 text-gray-900">
                    <Highlight text={item.title} query={query} />
                </span>

                {item.sub && (
                    <span
                        dir="ltr"
                        className="mt-1.5 block truncate text-left text-[11px] text-gray-500"
                    >
                        P/N: <Highlight text={item.sub} query={query} />
                    </span>
                )}

                {item.kind === "file" && (
                    <span className="mt-1.5 block text-[11px] text-gray-500">باز شدن در تب جدید</span>
                )}
            </span>
        </>
    );

    const common = {
        id: `searchbox-item-${idPrefix}${index}`,
        href: item.href,
        onClick: onOpen,
        onMouseEnter: () => onHover(index),
        className,
    };

    return item.external ? (
        <a {...common} target="_blank" rel="noopener noreferrer">
            {body}
        </a>
    ) : (
        <Link {...common}>{body}</Link>
    );
};

const GRID_CLASS = "grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-4";
/** گرید تمام‌صفحه‌ی موبایل: ۲ ستون، روی عرض‌های بزرگ‌تر ۳ ستون */
const GRID_MOBILE = "grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3";

const SkeletonGrid = ({ grid = GRID_CLASS }: { grid?: string }) => (
    <div className={grid} aria-hidden="true">
        {Array.from({ length: 8 }, (_, i) => (
            <div
                key={i}
                className="flex animate-pulse flex-col gap-3 rounded-xl border border-gray-200 p-3 dark:border-white/10"
            >
                <div className="aspect-square w-full rounded-lg bg-gray-200 dark:bg-white/10" />
                <div className="space-y-2">
                    <div className="h-3 w-4/5 rounded bg-gray-200 dark:bg-white/10" />
                    <div className="h-3 w-1/2 rounded bg-gray-200 dark:bg-white/10" />
                </div>
            </div>
        ))}
    </div>
);

/* ================================
   Main
================================ */

const SearchBox = () => {
    const router = useRouter();
    const searchParams = useSearchParams();

    const keywordFromUrl = searchParams.get("keyword") || searchParams.get("search") || "";

    const [query, setQuery] = useState("");
    const [data, setData] = useState<Suggestions>(EMPTY_SUGGESTIONS);
    const [loading, setLoading] = useState(false);
    /** اسکرول بی‌نهایت: تعداد کل هر بخش، شماره‌ی آخرین صفحه‌ی لودشده، و وضعیت لودینگ صفحه‌ی بعد */
    const [totals, setTotals] = useState<SearchTotals>(EMPTY_TOTALS);
    const [pages, setPages] = useState<Record<Kind, number>>(FIRST_PAGES);
    const [loadingMore, setLoadingMore] = useState(false);
    const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState<TabKey>("products");
    const [activeIndex, setActiveIndex] = useState(-1);
    /** با افزایش این عدد، جستجو حتی برای عبارتِ هم‌گام با URL دوباره اجرا می‌شود */
    const [nonce, setNonce] = useState(0);
    /** پنل تمام‌صفحه‌ی موبایل */
    const [mobileOpen, setMobileOpen] = useState(false);
    /** جستجوهای اخیر */
    const [recent, setRecent] = useState<string[]>([]);
    const [recentActive, setRecentActive] = useState(-1);
    const recentRef = useRef<string[]>([]);
    const lastLoadRef = useRef(0);
    const lastSavedRef = useRef<{ term: string; at: number } | null>(null);
    /** موقعیت پنل دسکتاپ؛ همیشه وسط صفحه و داخل viewport */
    const [panelPos, setPanelPos] = useState<{ left: number; width: number } | null>(null);

    const rootRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const mobileInputRef = useRef<HTMLInputElement>(null);
    const requestId = useRef(0);
    /** لغو درخواست قبلی وقتی کاربر به تایپ ادامه می‌دهد */
    const abortRef = useRef<AbortController | null>(null);
    /**
     * عبارتی که از آدرس صفحه (مثلاً /search?keyword=...) آمده است؛
     * برای آن خودکار جستجو/باز شدن پنل انجام نمی‌شود، مگر کاربر روی فیلد کلیک کند.
     */
    const syncedFromUrl = useRef<string | null>(null);

    const q = query.trim();
    const panelVisible = open && q.length > 0;
    /** پاپ‌آپ جستجوهای اخیر: وقتی فیلد فوکوس دارد و خالی است */
    const recentVisible = open && q.length === 0 && recent.length > 0;

    /* ---------- هم‌گام‌سازی با آدرس ---------- */
    useEffect(() => {
        syncedFromUrl.current = keywordFromUrl || null;
        setQuery(keywordFromUrl);
        setOpen(false);
    }, [keywordFromUrl]);

    /* ---------- جستجوی پیشنهادی (debounce) ---------- */
    useEffect(() => {
        if (!q) {
            requestId.current++;
            abortRef.current?.abort();
            setData(EMPTY_SUGGESTIONS);
            setTotals(EMPTY_TOTALS);
            setPages(FIRST_PAGES);
            setLoadingMore(false);
            setLoading(false);
            return;
        }

        if (query === syncedFromUrl.current) {
            requestId.current++;
            abortRef.current?.abort();
            setLoading(false);
            return;
        }

        const id = ++requestId.current;
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        setLoadingMore(false);

        const timer = setTimeout(async () => {
            try {
                const res = await suggestSearch(q, 1, PAGE_SIZE, undefined, controller.signal);
                if (id !== requestId.current) return;
                // برای عیب‌یابی آدرس عکس‌ها (بعد از رفع مشکل پاک کنید)
                // console.log(res.products[0], res.posts[0]);
                setData(res);
                setTotals(res.totals);
                setPages(FIRST_PAGES);
                // مکث کاربر + آمدن نتیجه: همان لحظه در دیتابیس ثبت می‌شود
                if (res.products.length + res.posts.length + res.files.length > 0) addRecent(q);
                setTab(pickDefaultTab(res));
                setActiveIndex(-1);
            } catch (error) {
                console.error(error);
                if (id === requestId.current) {
                    setData(EMPTY_SUGGESTIONS);
                    setTotals(EMPTY_TOTALS);
                }
            } finally {
                if (id === requestId.current) setLoading(false);
            }
        }, 150);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, nonce]);

    /* ---------- بستن با کلیک بیرون ---------- */
    useEffect(() => {
        const onMouseDown = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onMouseDown);
        return () => document.removeEventListener("mousedown", onMouseDown);
    }, []);

    /* ---------- میان‌بر «/» برای رفتن به فیلد جستجو ---------- */
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey) return;
            const el = e.target as HTMLElement | null;
            const tag = el?.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el?.isContentEditable) return;
            e.preventDefault();
            inputRef.current?.focus();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, []);

    /* ---------- داده‌ی نمایشی ---------- */
    const allItems = useMemo<ResultItem[]>(
        () => [
            ...data.products.map<ResultItem>((p) => ({
                key: `product-${p.id}`,
                kind: "product",
                title: p.name,
                sub: p.sku || undefined,
                href: `/shop/${p.slug}`,
                image: imageUrl("product", pickImage(p)),
            })),
            ...data.posts.map<ResultItem>((p) => ({
                key: `post-${p.id}`,
                kind: "post",
                title: p.title,
                href: `/blog/${p.slug}`,
                image: imageUrl("post", pickImage(p)),
            })),
            ...data.files.map<ResultItem>((f, i) => ({
                key: `file-${i}`,
                kind: "file",
                title: f.name,
                href: `${STORAGE}/pdf/${f.name}`,
                external: true,
            })),
        ],
        [data],
    );

    /** شمارنده‌ها از «کل» نتایج سرور می‌آیند، نه فقط موارد لودشده */
    const total = {
        products: Math.max(totals.products, data.products.length),
        posts: Math.max(totals.posts, data.posts.length),
        files: Math.max(totals.files, data.files.length),
    };
    const counts: Record<TabKey, number> = {
        all: total.products + total.posts + total.files,
        products: total.products,
        posts: total.posts,
        files: total.files,
    };

    /** در تب‌های تک‌دسته (نه «همه») اگر هنوز مورد لودنشده‌ای هست */
    const activeKind: Kind | null = tab === "all" ? null : TAB_KIND[tab];
    const hasMore = activeKind
        ? data[KIND_META[activeKind].tab].length < total[KIND_META[activeKind].tab]
        : false;

    /** لیست نهایی نمایش (ترتیب آن همان ترتیب ناوبری با کیبورد است) */
    const visible = useMemo<ResultItem[]>(() => {
        if (tab !== "all") return allItems.filter((i) => i.kind === TAB_KIND[tab]);

        const used: Record<Kind, number> = { product: 0, post: 0, file: 0 };
        return allItems.filter((i) => used[i.kind]++ < ALL_TAB_LIMIT[i.kind]);
    }, [allItems, tab]);

    const groups = KIND_ORDER.map((kind) => ({
        kind,
        entries: visible
            .map((item, index) => ({ item, index }))
            .filter((e) => e.item.kind === kind),
    })).filter((g) => g.entries.length > 0);

    useEffect(() => {
        if (activeIndex < 0) return;
        document
            .getElementById(`searchbox-item-${mobileOpen ? "m-" : "d-"}${activeIndex}`)
            ?.scrollIntoView({ block: "nearest" });
    }, [activeIndex, mobileOpen]);

    /* ---------- اسکرول بی‌نهایت: وقتی انتهای لیست دیده شد، صفحه‌ی بعد لود شود ---------- */
    useEffect(() => {
        if (!sentinel || !activeKind || !hasMore) return;

        const io = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) void loadMore(activeKind);
            },
            { rootMargin: "240px 0px" },
        );
        io.observe(sentinel);
        return () => io.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sentinel, activeKind, hasMore, loadingMore, loading, data, q]);

    /* ---------- موقعیت پنل دسکتاپ (وسط viewport، بدون بیرون‌زدگی در تبلت) ---------- */
    useLayoutEffect(() => {
        if (!panelVisible) return;
        const calc = () => {
            const el = rootRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const vw = document.documentElement.clientWidth;
            const width = Math.min(1100, vw - 32);
            setPanelPos({ width, left: (vw - width) / 2 - rect.left });
        };
        calc();
        window.addEventListener("resize", calc);
        return () => window.removeEventListener("resize", calc);
    }, [panelVisible]);

    /* ---------- پنل موبایل: قفل اسکرول، فوکوس، Esc ---------- */
    useEffect(() => {
        if (!mobileOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const t = setTimeout(() => mobileInputRef.current?.focus(), 50);
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setMobileOpen(false);
        };
        document.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prev;
            clearTimeout(t);
            document.removeEventListener("keydown", onKey);
        };
    }, [mobileOpen]);

    /* اگر صفحه به عرض دسکتاپ رسید (چرخش تبلت / تغییر اندازه)، پنل موبایل بسته شود */
    useEffect(() => {
        const mq = window.matchMedia("(min-width: 768px)");
        const onChange = () => {
            if (mq.matches) setMobileOpen(false);
        };
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);

    /* ---------- اکشن‌ها ---------- */
    /** لود صفحه‌ی بعدی یک دسته (اسکرول بی‌نهایت / AJAX) */
    const loadMore = async (kind: Kind) => {
        const key = KIND_META[kind].tab;
        if (!q || loadingMore || loading) return;
        if (data[key].length >= total[key]) return;

        const myRequest = requestId.current;
        const nextPage = pages[kind] + 1;
        setLoadingMore(true);

        try {
            const res = await suggestSearch(q, nextPage, PAGE_SIZE, key, abortRef.current?.signal);
            if (myRequest !== requestId.current) return; // عبارت عوض شده است
            setData(
                (prev) =>
                    ({ ...prev, [key]: mergeUnique(prev[key] as unknown[], res[key] as unknown[]) }) as Suggestions,
            );
            setPages((p) => ({ ...p, [kind]: nextPage }));
            // اگر سرور چیزی برنگرداند، از تلاش‌های بی‌پایان جلوگیری می‌شود
            if (res[key].length === 0) setTotals((t) => ({ ...t, [key]: data[key].length }));
        } catch (error) {
            console.error(error);
        } finally {
            if (myRequest === requestId.current) setLoadingMore(false);
        }
    };

    /** خواندن لیست از دیتابیس (حداکثر هر ۵ ثانیه یک بار مگر force) */
    const loadRecent = async (force = false) => {
        const now = Date.now();
        if (!force && now - lastLoadRef.current < 5000) return;
        lastLoadRef.current = now;

        const list = await fetchRecentSearches(RECENT_MAX);
        if (list) {
            recentRef.current = list;
            setRecent(list);
        }
    };

    const setRecentLocal = (list: string[]) => {
        recentRef.current = list;
        setRecent(list);
        setRecentActive(-1);
    };

    /** ثبت عبارت: فوری در لیست محلی و همزمان در دیتابیس */
    const addRecent = (term: string) => {
        const t = term.replace(/\s+/g, " ").trim();
        if (t.length < 2) return;

        const prev = lastSavedRef.current;
        if (prev && prev.term === t) return;

        // کاربر هنوز در حال تایپ/اصلاح همان عبارت است؟ نسخه‌ی ناقص قبلی جایگزین شود
        const replaces =
            prev &&
            Date.now() - prev.at < RECENT_REPLACE_WINDOW_MS &&
            (t.toLowerCase().startsWith(prev.term.toLowerCase()) ||
                prev.term.toLowerCase().startsWith(t.toLowerCase()))
                ? prev.term
                : undefined;

        lastSavedRef.current = { term: t, at: Date.now() };
        setRecentLocal([t, ...recentRef.current.filter((x) => x !== t && x !== replaces)].slice(0, RECENT_MAX));
        void saveRecentSearch(t, replaces);
    };

    const removeRecent = (term: string) => {
        if (lastSavedRef.current?.term === term) lastSavedRef.current = null;
        setRecentLocal(recentRef.current.filter((x) => x !== term));
        void deleteRecentSearch(term);
    };

    const clearRecent = () => {
        lastSavedRef.current = null;
        setRecentLocal([]);
        void clearRecentSearches();
    };

    /** انتخاب یک مورد از جستجوهای اخیر: عبارت در فیلد قرار می‌گیرد و نتایج نمایش داده می‌شود */
    const pickRecent = (term: string, mobile: boolean) => {
        syncedFromUrl.current = null;
        setQuery(term);
        setOpen(true);
        setRecentActive(-1);
        (mobile ? mobileInputRef : inputRef).current?.focus();
    };

    const closeAndReset = () => {
        syncedFromUrl.current = null;
        setOpen(false);
        setMobileOpen(false);
        setQuery("");
        setData(EMPTY_SUGGESTIONS);
        setTotals(EMPTY_TOTALS);
        setPages(FIRST_PAGES);
        setTab("products");
        setActiveIndex(-1);
    };

    /** کلیک روی یکی از نتایج: عبارت به اخیرها اضافه می‌شود */
    const handleOpenResult = () => {
        addRecent(q);
        closeAndReset();
    };

    const performSearch = () => {
        if (!q) return;
        addRecent(q);
        setOpen(false);
        setMobileOpen(false);
        router.push(`/search?keyword=${encodeURIComponent(q)}`);
    };

    const clearSearch = () => {
        closeAndReset();
        if (window.location.pathname === "/search") {
            router.push("/search");
        }
        inputRef.current?.focus();
    };

    const openMobile = () => {
        if (q && query === syncedFromUrl.current) {
            syncedFromUrl.current = null;
            setNonce((n) => n + 1);
        }
        setMobileOpen(true);
        if (!q) void loadRecent();
    };

    const handleFocus = () => {
        if (!q) {
            setOpen(true);
            void loadRecent();
            return;
        }
        if (query === syncedFromUrl.current) {
            syncedFromUrl.current = null;
            setNonce((n) => n + 1);
        }
        setOpen(true);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (recentVisible) {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setRecentActive((i) => (i + 1) % recent.length);
                return;
            }
            if (e.key === "ArrowUp") {
                e.preventDefault();
                setRecentActive((i) => (i <= 0 ? recent.length - 1 : i - 1));
                return;
            }
            if (e.key === "Enter" && recentActive >= 0) {
                e.preventDefault();
                pickRecent(recent[recentActive], false);
                return;
            }
        }

        const count = visible.length;

        if (e.key === "ArrowDown" && panelVisible && count > 0) {
            e.preventDefault();
            setActiveIndex((i) => (i + 1) % count);
        } else if (e.key === "ArrowUp" && panelVisible && count > 0) {
            e.preventDefault();
            setActiveIndex((i) => (i <= 0 ? count - 1 : i - 1));
        } else if (e.key === "Enter") {
            const item = panelVisible && activeIndex >= 0 ? visible[activeIndex] : undefined;
            if (item) {
                e.preventDefault();
                addRecent(q);
                if (item.external) {
                    window.open(item.href, "_blank", "noopener,noreferrer");
                    setOpen(false);
                } else {
                    router.push(item.href);
                    closeAndReset();
                }
            } else {
                performSearch();
            }
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
        { key: "all", label: "همه", icon: <LayoutGrid className="h-4 w-4" aria-hidden="true" /> },
        { key: "products", label: "محصولات", icon: <Package className="h-4 w-4" aria-hidden="true" /> },
        { key: "posts", label: "مقالات", icon: <FileText className="h-4 w-4" aria-hidden="true" /> },
        { key: "files", label: "فایل‌ها", icon: <FileIcon className="h-4 w-4" aria-hidden="true" /> },
    ];

    const hasResults = allItems.length > 0;
    const showSkeleton = loading && !hasResults;

    const renderTabs = (mobile: boolean) => (
        <nav
            className={mobile ? "flex gap-2 overflow-x-auto px-3 py-2.5 [scrollbar-width:none]" : "space-y-1"}
            aria-label="دسته‌بندی نتایج"
        >
            {tabs.map((t) => {
                const isActive = tab === t.key;
                const count = counts[t.key];
                const disabled = t.key !== "all" && count === 0;
                return (
                    <button
                        key={t.key}
                        type="button"
                        disabled={disabled}
                        onClick={() => {
                            setTab(t.key);
                            setActiveIndex(-1);
                        }}
                        className={
                            (mobile
                                ? "flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-medium transition "
                                : "flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-[13px] font-medium transition ") +
                            (isActive
                                ? "bg-[#fff] text-primary shadow-sm ring-1 ring-gray-200 dark:bg-indigo-400/15 dark:text-white dark:ring-indigo-300/30 " +
                                  (mobile ? "border-primary/30 dark:border-indigo-300/30" : "border-transparent")
                                : "text-gray-600 hover:bg-[#fff]/70 dark:hover:bg-white/5 " +
                                  (mobile ? "border-gray-200 dark:border-white/10" : "border-transparent")) +
                            (disabled ? " cursor-not-allowed opacity-40" : " cursor-pointer")
                        }
                    >
                        {t.icon}
                        <span className={mobile ? "" : "flex-1 text-right"}>{t.label}</span>
                        <span
                            className={
                                "rounded-full px-1.5 py-0.5 text-[10px] font-bold " +
                                (isActive
                                    ? "bg-primary/10 text-primary dark:bg-indigo-300/20 dark:text-indigo-100"
                                    : "bg-gray-200/80 text-gray-600 dark:bg-white/10")
                            }
                        >
                            {formatNumber(count)}
                        </span>
                    </button>
                );
            })}
        </nav>
    );

    const renderRecent = (mobile: boolean) => (
        <div>
            <div className="flex items-center justify-between px-1 pb-2">
                <h3 className="flex items-center gap-1.5 text-xs font-bold text-gray-500">
                    <History className="h-3.5 w-3.5" aria-hidden="true" />
                    جستجوهای اخیر
                </h3>
                <button
                    type="button"
                    onClick={clearRecent}
                    className="cursor-pointer text-xs font-medium text-primary hover:underline dark:text-indigo-200"
                >
                    پاک کردن همه
                </button>
            </div>

            <ul className="space-y-0.5">
                {recent.map((term, i) => (
                    <li key={term}>
                        <div
                            onMouseEnter={() => !mobile && setRecentActive(i)}
                            className={
                                "group flex items-center gap-1 rounded-lg px-2 py-1.5 transition " +
                                (!mobile && i === recentActive
                                    ? "bg-gray-100 dark:bg-white/10"
                                    : "hover:bg-gray-100 dark:hover:bg-white/5")
                            }
                        >
                            <button
                                type="button"
                                onClick={() => pickRecent(term, mobile)}
                                className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 py-1 text-right"
                            >
                                <History className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                                <span className="truncate text-sm text-gray-800">{term}</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => removeRecent(term)}
                                aria-label={`حذف «${term}» از جستجوهای اخیر`}
                                title="حذف"
                                className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-500 transition hover:bg-rose-500/10 hover:text-rose-500"
                            >
                                <X className="h-3.5 w-3.5" aria-hidden="true" />
                            </button>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );

    const renderResults = (grid: string, idPrefix: string) => (
        <>
            {showSkeleton ? (
                                        <SkeletonGrid grid={grid} />
                                    ) : !hasResults ? (
                                        <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
                                            <span className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 dark:bg-white/5">
                                                <SearchX className="h-6 w-6 text-gray-400" aria-hidden="true" />
                                            </span>
                                            <p className="text-sm font-semibold text-gray-800">
                                                نتیجه‌ای برای «{q}» پیدا نشد
                                            </p>
                                            <p className="mt-1 text-xs leading-6 text-gray-500">
                                                عبارت کوتاه‌تر، نام فنی یا پارت‌نامبر محصول را امتحان کنید.
                                            </p>
                                        </div>
                                    ) : (
                                        groups.map((group) => (
                                            <div key={group.kind}>
                                                <div className="mb-2.5 flex items-center justify-between">
                                                    <h3 className="text-xs font-bold text-gray-500">
                                                        {KIND_META[group.kind].label}
                                                    </h3>
                                                    {tab === "all" &&
                                                        counts[KIND_META[group.kind].tab] > group.entries.length && (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setTab(KIND_META[group.kind].tab);
                                                                    setActiveIndex(-1);
                                                                }}
                                                                className="cursor-pointer text-xs font-medium text-primary hover:underline dark:text-indigo-200"
                                                            >
                                                                نمایش همه {formatNumber(counts[KIND_META[group.kind].tab])} مورد
                                                            </button>
                                                        )}
                                                </div>

                                                <div className={grid}>
                                                    {group.entries.map(({ item, index }) => (
                                                        <ResultCard
                                                            key={item.key}
                                                            item={item}
                                                            index={index}
                                                            active={index === activeIndex}
                                                            query={q}
                                                            idPrefix={idPrefix}
                                                            onHover={setActiveIndex}
                                                            onOpen={handleOpenResult}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        ))
                                    )}

            {/* انتهای لیست: با دیده شدن آن صفحه‌ی بعد لود می‌شود + اسپینر دایره‌ای */}
            {hasResults && activeKind && (hasMore || loadingMore) && (
                <div
                    ref={setSentinel}
                    className="flex h-14 items-center justify-center"
                    role="status"
                    aria-live="polite"
                >
                    <LoaderCircle
                        className={"h-6 w-6 text-primary dark:text-indigo-200 " + (loadingMore ? "animate-spin" : "opacity-40")}
                        aria-label="در حال بارگذاری نتایج بیشتر"
                    />
                </div>
            )}
        </>
    );

    const mobileOverlay = (
        <div
            className="fixed inset-0 z-[100] flex h-[100dvh] flex-col bg-[#fff] md:hidden dark:bg-[#0b1020]"
            role="dialog"
            aria-modal="true"
            aria-label="جستجو"
        >
            {/* نوار بالا: فیلد + بستن */}
            <div className="flex items-center gap-2 border-b border-gray-200 px-3 pb-2.5 pt-[max(0.625rem,env(safe-area-inset-top))] dark:border-white/10">
                <div className="relative flex-1">
                    <Search
                        className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                        size={18}
                        aria-hidden="true"
                    />
                    <input
                        ref={mobileInputRef}
                        type="text"
                        inputMode="search"
                        enterKeyHint="search"
                        autoComplete="off"
                        placeholder="جستجو در محصولات، مقالات و فایل‌ها ..."
                        value={query}
                        onChange={(e) => {
                            syncedFromUrl.current = null;
                            setQuery(e.target.value);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") performSearch();
                        }}
                        className="h-11 w-full rounded-xl border border-transparent bg-gray-100 pr-10 pl-10 text-base text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-primary/30 focus:ring-4 focus:ring-primary/10 dark:bg-white/[0.06] dark:focus:border-indigo-300/30 dark:focus:ring-indigo-300/10"
                    />
                    <div className="absolute left-2 top-1/2 flex -translate-y-1/2 items-center">
                        {loading ? (
                            <LoaderCircle className="h-4 w-4 animate-spin text-gray-400" aria-label="در حال جستجو" />
                        ) : query ? (
                            <button
                                type="button"
                                onClick={() => {
                                    closeAndReset();
                                    setMobileOpen(true);
                                    mobileInputRef.current?.focus();
                                }}
                                aria-label="پاک کردن"
                                className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 active:bg-gray-200"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        ) : null}
                    </div>
                </div>

                <button
                    type="button"
                    onClick={() => setMobileOpen(false)}
                    className="shrink-0 rounded-lg px-2.5 py-2 text-sm font-medium text-gray-600 active:bg-gray-200"
                >
                    بستن
                </button>
            </div>

            {q ? (
                <>
                    {/* تب‌ها (افقی و قابل اسکرول) */}
                    <div className="border-b border-gray-200 dark:border-white/10">{renderTabs(true)}</div>

                    {/* نتایج */}
                    <div
                        className={
                            "flex-1 space-y-5 overflow-y-auto overscroll-contain p-3 transition-opacity " +
                            (loading && hasResults ? "opacity-60" : "opacity-100")
                        }
                    >
                        {renderResults(GRID_MOBILE, "m-")}
                    </div>

                    <div className="border-t border-gray-200 bg-gray-50/80 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 dark:border-white/10 dark:bg-white/[0.02]">
                        <button
                            type="button"
                            onClick={performSearch}
                            className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-semibold text-white transition active:bg-primary-700 dark:bg-[#4451A3] dark:active:bg-[#5663c4]"
                        >
                            مشاهده همه نتایج
                            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                        </button>
                    </div>
                </>
            ) : recent.length > 0 ? (
                <div className="flex-1 overflow-y-auto overscroll-contain p-3">{renderRecent(true)}</div>
            ) : (
                <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
                    <span className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 dark:bg-white/5">
                        <Search className="h-6 w-6 text-gray-400" aria-hidden="true" />
                    </span>
                    <p className="text-sm font-semibold text-gray-800">عبارت مورد نظر را بنویسید</p>
                    <p className="mt-1 text-xs leading-6 text-gray-500">
                        نام محصول، پارت‌نامبر، عنوان مقاله یا نام فایل
                    </p>
                </div>
            )}
        </div>
    );

    return (
        <div className="flex items-center md:w-1/2">
            {/* ---------- موبایل: فقط دکمه‌ی جستجو ---------- */}
            <button
                type="button"
                onClick={openMobile}
                aria-label="جستجو"
                title="جستجو"
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg text-gray-700 transition hover:bg-gray-200 hover:text-primary md:hidden"
            >
                <Search className="h-5 w-5" aria-hidden="true" />
            </button>
            {mobileOpen && typeof document !== "undefined" && createPortal(mobileOverlay, document.body)}

            {/* ---------- تبلت/دسکتاپ ---------- */}
            <div className="hidden w-full items-center md:flex">
            <div className="relative w-full" ref={rootRef}>
                {/* ---------- فیلد جستجو ---------- */}
                <Search
                    className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
                    size={18}
                    aria-hidden="true"
                />

                <input
                    ref={inputRef}
                    type="text"
                    role="combobox"
                    aria-expanded={panelVisible}
                    // eslint-disable-next-line jsx-a11y/role-supports-aria-props
                    aria-autocomplete="list"
                    autoComplete="off"
                    placeholder="جستجو در محصولات، مقالات و فایل‌ها ..."
                    value={query}
                    onChange={(e) => {
                        syncedFromUrl.current = null;
                        setQuery(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={handleFocus}
                    onClick={() => {
                        setOpen(true);
                        if (!q) void loadRecent();
                    }}
                    onKeyDown={handleKeyDown}
                    className="h-11 w-full rounded-xl border border-transparent bg-gray-100 pr-11 pl-24 text-sm
                               text-gray-900 outline-none transition placeholder:text-gray-400
                               focus:border-primary/30 focus:bg-white focus:ring-4 focus:ring-primary/10
                               dark:bg-white/[0.06] dark:focus:border-indigo-300/30 dark:focus:bg-white/10 dark:focus:ring-indigo-300/10"
                />

                <div className="absolute left-2 top-1/2 flex -translate-y-1/2 items-center gap-1.5">
                    {loading ? (
                        <LoaderCircle className="h-4 w-4 animate-spin text-gray-400" aria-label="در حال جستجو" />
                    ) : !query ? (
                        <kbd className="hidden rounded-md border border-gray-300 bg-[#fff] px-1.5 py-0.5 font-mono text-[11px] text-gray-400 lg:block dark:border-white/15 dark:bg-white/10">
                            /
                        </kbd>
                    ) : null}

                    {query && (
                        <button
                            type="button"
                            onClick={clearSearch}
                            aria-label="پاک کردن"
                            title="پاک کردن"
                            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-200"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}

                    {query && (
                        <button
                            type="button"
                            onClick={performSearch}
                            aria-label="جستجو"
                            title="جستجو"
                            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-primary text-white transition hover:bg-primary-700 dark:bg-[#4451A3] dark:hover:bg-[#5663c4]"
                        >
                            <ArrowLeft className="h-4 w-4" />
                        </button>
                    )}
                </div>

                {/* ---------- پاپ‌آپ جستجوهای اخیر ---------- */}
                {recentVisible && (
                    <div
                        className="absolute inset-x-0 top-full z-50 mt-2 max-h-[min(26rem,70vh)] overflow-y-auto rounded-2xl border border-gray-200 bg-[#fff] p-3 shadow-2xl shadow-gray-900/10
                                   dark:border-white/10 dark:bg-[#0e1325] dark:shadow-black/60 dark:ring-1 dark:ring-white/5"
                    >
                        {renderRecent(false)}
                    </div>
                )}

                {/* ---------- پنل نتایج ---------- */}
                {panelVisible && (
                    <div
                        style={panelPos ? { left: panelPos.left, width: panelPos.width } : { left: 0, width: "100%" }}
                        className="absolute top-full z-50 mt-3 max-w-[calc(100vw-2rem)]
                                   overflow-hidden rounded-2xl border border-gray-200 bg-[#fff] shadow-2xl shadow-gray-900/10
                                   dark:border-white/10 dark:bg-[#0e1325] dark:shadow-black/60 dark:ring-1 dark:ring-white/5"
                    >
                        <div className="flex">
                            {/* ستون فیلتر (سمت راست) */}
                            <aside className="w-40 shrink-0 border-l border-gray-200 bg-gray-50 p-3 lg:w-48 dark:border-white/10 dark:bg-white/[0.03]">
                                <div className="mb-3 flex flex-1 gap-2">
                                    <p className="text-[11px] font-semibold text-gray-400">نتایج برای</p>
                                    <p className="truncate text-sm font-bold text-gray-900" title={q}>
                                        «{q}»
                                    </p>
                                </div>

                                {renderTabs(false)}
                            </aside>

                            {/* ناحیه‌ی نتایج (گرید) */}
                            <section className="flex min-w-0 flex-1 flex-col">
                                <div
                                    className={
                                        "max-h-[min(34rem,70vh)] flex-1 space-y-5 overflow-y-auto p-4 transition-opacity [scrollbar-width:thin] [scrollbar-color:rgba(148,163,184,0.35)_transparent] " +
                                        (loading && hasResults ? "opacity-60" : "opacity-100")
                                    }
                                >
                                    {renderResults(GRID_CLASS, "d-")}
                                </div>

                                <div className="flex items-center justify-between gap-3 border-t border-gray-200 bg-gray-50/80 px-4 py-2.5 dark:border-white/10 dark:bg-white/[0.02]">
                                    <div className="hidden items-center gap-3 text-[11px] text-gray-400 lg:flex">
                                        <span className="flex items-center gap-1">
                                            <kbd className="rounded border border-gray-300 bg-[#fff] px-1 dark:border-white/15 dark:bg-white/10">↑</kbd>
                                            <kbd className="rounded border border-gray-300 bg-[#fff] px-1 dark:border-white/15 dark:bg-white/10">↓</kbd>
                                            حرکت
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <kbd className="rounded border border-gray-300 bg-[#fff] px-1 dark:border-white/15 dark:bg-white/10">Enter</kbd>
                                            باز کردن
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <kbd className="rounded border border-gray-300 bg-[#fff] px-1 dark:border-white/15 dark:bg-white/10">Esc</kbd>
                                            بستن
                                        </span>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={performSearch}
                                        className="mr-auto flex cursor-pointer items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-primary transition hover:bg-primary/10 dark:text-indigo-200 dark:hover:bg-white/10"
                                    >
                                        مشاهده همه نتایج
                                        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
                                    </button>
                                </div>
                            </section>
                        </div>
                    </div>
                )}
            </div>
            </div>
        </div>
    );
};

export default SearchBox;
